import { TopicGroup } from '../algebra1/lessons';

export const anglesAndLines: TopicGroup = {
  id: 'angles-lines',
  title: 'Angles & Lines',
  icon: 'ri-ruler-line',
  lessons: [
    {
      id: 'angle-basics',
      title: 'Angle Basics & Classification',
      description: 'Master angle types, measurement, and relationships',
      textbookExplanation: `
# Understanding Angles

An **angle** is formed when two rays share a common endpoint called the **vertex**. Angles are measured in degrees (°).

## Angle Classification by Measure:
- **Acute angle**: measures between 0° and 90°
- **Right angle**: measures exactly 90°
- **Obtuse angle**: measures between 90° and 180°
- **Straight angle**: measures exactly 180°
- **Reflex angle**: measures between 180° and 360°

## Special Angle Pairs:
- **Complementary angles**: two angles that sum to 90°
- **Supplementary angles**: two angles that sum to 180°
- **Vertical angles**: opposite angles formed by two intersecting lines (always equal)
- **Adjacent angles**: angles that share a common side and vertex

## Linear Pair:
When two adjacent angles form a straight line, they are supplementary (sum to 180°).
      `,
      shortMethod: `
# Mr. Augustine's Angle Shortcuts

**The "Quick Classification" Method:**
- Less than 90° → Acute (think: "a-cute little angle")
- Exactly 90° → Right (the corner of a square)
- Between 90° and 180° → Obtuse (think: "obese" = bigger)
- Exactly 180° → Straight (a line)

**The "Complement/Supplement Trick":**
- Complementary = 90° (C comes before S, 90 comes before 180)
- To find complement: 90 - angle
- To find supplement: 180 - angle

**The "Vertical Angles Are Twins" Rule:**
When lines cross, opposite angles are identical. No calculation needed!

**The "Linear Pair = 180°" Shortcut:**
If two angles are next to each other on a line, just subtract from 180°.
      `,
      workedExamples: [
        {
          problem: 'Find the complement and supplement of a 35° angle.',
          solution: `
**Step 1:** Find the complement (sum to 90°)
Complement = 90° - 35° = 55°

**Step 2:** Find the supplement (sum to 180°)
Supplement = 180° - 35° = 145°

**Answer:** Complement is 55°, supplement is 145°
          `
        },
        {
          problem: 'Two angles form a linear pair. One angle measures 112°. Find the other angle.',
          solution: `
**Step 1:** Recall that a linear pair sums to 180°

**Step 2:** Subtract from 180°
Other angle = 180° - 112° = 68°

**Answer:** The other angle measures 68°
          `
        },
        {
          problem: 'Lines AB and CD intersect. If angle 1 measures 73°, find the measure of the vertical angle.',
          solution: `
**Step 1:** Recall that vertical angles are equal

**Step 2:** The vertical angle also measures 73°

**Answer:** 73° (vertical angles are always congruent)
          `
        }
      ],
      practiceQuestions: [
        {
          id: 1,
          question: 'An angle measures 42°. What type of angle is it?',
          options: ['Acute', 'Right', 'Obtuse', 'Straight'],
          correctAnswer: 0,
          solution: 'Since 42° is less than 90°, it is an acute angle.'
        },
        {
          id: 2,
          question: 'What is the complement of a 28° angle?',
          options: ['52°', '62°', '72°', '152°'],
          correctAnswer: 1,
          solution: 'Complement = 90° - 28° = 62°'
        },
        {
          id: 3,
          question: 'What is the supplement of a 115° angle?',
          options: ['25°', '45°', '65°', '75°'],
          correctAnswer: 2,
          solution: 'Supplement = 180° - 115° = 65°'
        },
        {
          id: 4,
          question: 'Two angles form a linear pair. One measures 83°. Find the other.',
          options: ['7°', '83°', '97°', '107°'],
          correctAnswer: 2,
          solution: 'Linear pair sum to 180°. Other angle = 180° - 83° = 97°'
        },
        {
          id: 5,
          question: 'Vertical angles measure 5x and 3x + 40. Find x.',
          options: ['10', '15', '20', '25'],
          correctAnswer: 2,
          solution: 'Vertical angles are equal: 5x = 3x + 40, so 2x = 40, x = 20'
        },
        {
          id: 6,
          question: 'An angle measures 127°. What type is it?',
          options: ['Acute', 'Right', 'Obtuse', 'Straight'],
          correctAnswer: 2,
          solution: 'Since 127° is between 90° and 180°, it is obtuse.'
        },
        {
          id: 7,
          question: 'Two complementary angles are in ratio 2:3. Find the smaller angle.',
          options: ['30°', '36°', '45°', '54°'],
          correctAnswer: 1,
          solution: '2x + 3x = 90°, so 5x = 90°, x = 18°. Smaller = 2(18°) = 36°'
        },
        {
          id: 8,
          question: 'Two supplementary angles are equal. What is each angle?',
          options: ['45°', '60°', '90°', '120°'],
          correctAnswer: 2,
          solution: 'If equal and supplementary: 2x = 180°, so x = 90°'
        },
        {
          id: 9,
          question: 'Find the complement of 67°.',
          options: ['13°', '23°', '33°', '113°'],
          correctAnswer: 1,
          solution: 'Complement = 90° - 67° = 23°'
        },
        {
          id: 10,
          question: 'Lines intersect forming angles. If one is 48°, find its vertical angle.',
          options: ['42°', '48°', '132°', '138°'],
          correctAnswer: 1,
          solution: 'Vertical angles are equal, so it is also 48°'
        },
        {
          id: 11,
          question: 'An angle and its supplement are in ratio 1:4. Find the angle.',
          options: ['30°', '36°', '40°', '45°'],
          correctAnswer: 1,
          solution: 'x + 4x = 180°, so 5x = 180°, x = 36°'
        },
        {
          id: 12,
          question: 'What is the supplement of 92°?',
          options: ['2°', '88°', '98°', '178°'],
          correctAnswer: 1,
          solution: 'Supplement = 180° - 92° = 88°'
        },
        {
          id: 13,
          question: 'Two angles form a linear pair. One is twice the other. Find the larger.',
          options: ['60°', '90°', '120°', '150°'],
          correctAnswer: 2,
          solution: 'x + 2x = 180°, so 3x = 180°, x = 60°. Larger = 2(60°) = 120°'
        },
        {
          id: 14,
          question: 'An angle measures exactly 90°. What type is it?',
          options: ['Acute', 'Right', 'Obtuse', 'Straight'],
          correctAnswer: 1,
          solution: 'An angle of exactly 90° is a right angle.'
        },
        {
          id: 15,
          question: 'Find the complement of 81°.',
          options: ['9°', '19°', '99°', '109°'],
          correctAnswer: 0,
          solution: 'Complement = 90° - 81° = 9°'
        },
        {
          id: 16,
          question: 'Vertical angles measure 7x - 10 and 5x + 20. Find x.',
          options: ['10', '15', '20', '25'],
          correctAnswer: 1,
          solution: '7x - 10 = 5x + 20, so 2x = 30, x = 15'
        },
        {
          id: 17,
          question: 'Two complementary angles differ by 20°. Find the larger.',
          options: ['35°', '45°', '55°', '65°'],
          correctAnswer: 2,
          solution: 'x + (x + 20) = 90°, so 2x = 70°, x = 35°. Larger = 55°'
        },
        {
          id: 18,
          question: 'An angle measures 180°. What type is it?',
          options: ['Acute', 'Right', 'Obtuse', 'Straight'],
          correctAnswer: 3,
          solution: 'An angle of exactly 180° is a straight angle.'
        },
        {
          id: 19,
          question: 'What is the supplement of 45°?',
          options: ['45°', '90°', '135°', '145°'],
          correctAnswer: 2,
          solution: 'Supplement = 180° - 45° = 135°'
        },
        {
          id: 20,
          question: 'Two supplementary angles are in ratio 5:7. Find the smaller.',
          options: ['60°', '65°', '75°', '105°'],
          correctAnswer: 2,
          solution: '5x + 7x = 180°, so 12x = 180°, x = 15°. Smaller = 5(15°) = 75°'
        }
      ]
    },
    {
      id: 'parallel-lines',
      title: 'Parallel Lines & Transversals',
      description: 'Learn angle relationships when parallel lines are cut by a transversal',
      textbookExplanation: `
# Parallel Lines Cut by a Transversal

When a **transversal** (a line that crosses two or more lines) intersects **parallel lines**, special angle relationships are formed.

## Key Angle Pairs:

**1. Corresponding Angles:**
- Located in the same position at each intersection
- Always **equal** when lines are parallel
- Example: both upper-right angles

**2. Alternate Interior Angles:**
- Between the parallel lines, on opposite sides of the transversal
- Always **equal** when lines are parallel

**3. Alternate Exterior Angles:**
- Outside the parallel lines, on opposite sides of the transversal
- Always **equal** when lines are parallel

**4. Consecutive Interior Angles (Co-Interior):**
- Between the parallel lines, on the same side of the transversal
- Always **supplementary** (sum to 180°) when lines are parallel

## Important Theorem:
If corresponding angles are equal, the lines are parallel.
If alternate interior angles are equal, the lines are parallel.
      `,
      shortMethod: `
# Mr. Augustine's Parallel Lines Shortcuts

**The "F-Pattern" for Corresponding Angles:**
Draw an F through the parallel lines and transversal. The two angles in the F are equal!

**The "Z-Pattern" for Alternate Interior:**
Draw a Z through the diagram. The two angles at the ends of the Z are equal!

**The "C-Pattern" for Consecutive Interior:**
Draw a C on the same side. Those two angles add to 180°!

**The "One Angle = All Angles" Rule:**
Once you know ONE angle, you can find all 8 angles:
- 4 angles equal that angle (corresponding + alternate)
- 4 angles equal 180° minus that angle (supplements)

**Quick Memory Trick:**
- "Corresponding" = "Copy" (same angle)
- "Alternate" = "Also equal" 
- "Consecutive" = "Combine to 180°"
      `,
      workedExamples: [
        {
          problem: 'Parallel lines are cut by a transversal. One angle measures 65°. Find all other angles.',
          solution: `
**Step 1:** Identify the given angle (65°)

**Step 2:** Find equal angles (corresponding, alternate interior, alternate exterior)
Four angles = 65°

**Step 3:** Find supplementary angles (linear pairs)
Four angles = 180° - 65° = 115°

**Answer:** Four angles are 65°, four angles are 115°
          `
        },
        {
          problem: 'Lines m and n are parallel. A transversal creates an angle of 3x + 15 and a corresponding angle of 5x - 25. Find x.',
          solution: `
**Step 1:** Set up equation (corresponding angles are equal)
3x + 15 = 5x - 25

**Step 2:** Solve for x
15 + 25 = 5x - 3x
40 = 2x
x = 20

**Answer:** x = 20
          `
        },
        {
          problem: 'Consecutive interior angles measure 2x + 30 and 3x + 20. Find x.',
          solution: `
**Step 1:** Set up equation (consecutive interior angles sum to 180°)
(2x + 30) + (3x + 20) = 180

**Step 2:** Simplify and solve
5x + 50 = 180
5x = 130
x = 26

**Answer:** x = 26
          `
        }
      ],
      practiceQuestions: [
        {
          id: 1,
          question: 'Parallel lines cut by transversal. One angle is 72°. Find a corresponding angle.',
          options: ['72°', '108°', '118°', '128°'],
          correctAnswer: 0,
          solution: 'Corresponding angles are equal when lines are parallel. Answer: 72°'
        },
        {
          id: 2,
          question: 'One angle is 110°. Find an alternate interior angle.',
          options: ['70°', '80°', '110°', '120°'],
          correctAnswer: 2,
          solution: 'Alternate interior angles are equal. Answer: 110°'
        },
        {
          id: 3,
          question: 'Consecutive interior angles are 4x and 5x. Find x.',
          options: ['15', '20', '25', '30'],
          correctAnswer: 1,
          solution: '4x + 5x = 180°, so 9x = 180°, x = 20'
        },
        {
          id: 4,
          question: 'One angle measures 58°. Find its consecutive interior angle.',
          options: ['58°', '112°', '122°', '132°'],
          correctAnswer: 2,
          solution: 'Consecutive interior sum to 180°. 180° - 58° = 122°'
        },
        {
          id: 5,
          question: 'Corresponding angles are 6x - 10 and 4x + 30. Find x.',
          options: ['10', '15', '20', '25'],
          correctAnswer: 2,
          solution: '6x - 10 = 4x + 30, so 2x = 40, x = 20'
        },
        {
          id: 6,
          question: 'One angle is 135°. Find an alternate exterior angle.',
          options: ['45°', '55°', '135°', '145°'],
          correctAnswer: 2,
          solution: 'Alternate exterior angles are equal. Answer: 135°'
        },
        {
          id: 7,
          question: 'Alternate interior angles are 7x + 5 and 9x - 15. Find x.',
          options: ['5', '10', '15', '20'],
          correctAnswer: 1,
          solution: '7x + 5 = 9x - 15, so 20 = 2x, x = 10'
        },
        {
          id: 8,
          question: 'One angle is 48°. How many of the 8 angles equal 48°?',
          options: ['2', '3', '4', '6'],
          correctAnswer: 2,
          solution: 'Four angles equal 48° (corresponding + alternates), four equal 132°'
        },
        {
          id: 9,
          question: 'Consecutive interior angles are x + 40 and 2x - 10. Find x.',
          options: ['40', '45', '50', '55'],
          correctAnswer: 2,
          solution: '(x + 40) + (2x - 10) = 180, so 3x + 30 = 180, 3x = 150, x = 50'
        },
        {
          id: 10,
          question: 'One angle is 93°. Find a corresponding angle.',
          options: ['87°', '93°', '97°', '103°'],
          correctAnswer: 1,
          solution: 'Corresponding angles are equal. Answer: 93°'
        },
        {
          id: 11,
          question: 'Alternate interior angles are 5x and 3x + 40. Find x.',
          options: ['15', '20', '25', '30'],
          correctAnswer: 1,
          solution: '5x = 3x + 40, so 2x = 40, x = 20'
        },
        {
          id: 12,
          question: 'One angle is 124°. Find its consecutive interior angle.',
          options: ['46°', '56°', '66°', '76°'],
          correctAnswer: 1,
          solution: '180° - 124° = 56°'
        },
        {
          id: 13,
          question: 'Corresponding angles are 8x - 20 and 6x + 10. Find x.',
          options: ['10', '15', '20', '25'],
          correctAnswer: 1,
          solution: '8x - 20 = 6x + 10, so 2x = 30, x = 15'
        },
        {
          id: 14,
          question: 'One angle is 67°. Find an alternate interior angle.',
          options: ['23°', '67°', '113°', '123°'],
          correctAnswer: 1,
          solution: 'Alternate interior angles are equal. Answer: 67°'
        },
        {
          id: 15,
          question: 'Consecutive interior angles are 3x + 15 and 4x + 25. Find x.',
          options: ['15', '20', '25', '30'],
          correctAnswer: 1,
          solution: '(3x + 15) + (4x + 25) = 180, so 7x + 40 = 180, 7x = 140, x = 20'
        },
        {
          id: 16,
          question: 'One angle is 156°. How many angles equal 24°?',
          options: ['2', '3', '4', '6'],
          correctAnswer: 2,
          solution: 'Four angles equal 156°, four equal 180° - 156° = 24°'
        },
        {
          id: 17,
          question: 'Alternate exterior angles are 4x + 12 and 7x - 18. Find x.',
          options: ['5', '10', '15', '20'],
          correctAnswer: 1,
          solution: '4x + 12 = 7x - 18, so 30 = 3x, x = 10'
        },
        {
          id: 18,
          question: 'One angle is 38°. Find its consecutive interior angle.',
          options: ['38°', '52°', '142°', '152°'],
          correctAnswer: 2,
          solution: '180° - 38° = 142°'
        },
        {
          id: 19,
          question: 'Corresponding angles are 9x - 5 and 7x + 15. Find x.',
          options: ['5', '10', '15', '20'],
          correctAnswer: 1,
          solution: '9x - 5 = 7x + 15, so 2x = 20, x = 10'
        },
        {
          id: 20,
          question: 'Consecutive interior angles are in ratio 2:3. Find the smaller.',
          options: ['60°', '72°', '90°', '108°'],
          correctAnswer: 1,
          solution: '2x + 3x = 180°, so 5x = 180°, x = 36°. Smaller = 2(36°) = 72°'
        }
      ]
    }
  ]
};